import React, { useMemo } from "react";
import _ from "lodash";
import cx from "classnames";

import Slider from "../../../../components/slider";
import Wizard from "../../../../components/wizard";
import { Input } from "../../../../components/input";
import { Carousel } from "react-bootstrap";
import {
  ASSET_VIEWERS,
  DEFAULT_ASSET_VIEWER,
  DEFAULT_EXPERIENCE,
  EXPERIENCES,
} from "../../../../constants/collections";

import styles from "./create.module.scss";
import Button from "../../../../components/button";
import Reveal from "../../../../components/animation";
import subscriptionPlanService from "../../../../utils/subscriptionPlan.service";
import { useDispatch } from "react-redux";
import { createCollection, invalidateCollectionCreationState } from "../../../../redux/actions/collections";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Collection } from "../../../../types/collections";
import { animated, useTransition } from "@react-spring/web";
import { AddAssetsPanel } from "src/components/asset/add";
import { useCollectionsAdoption } from "src/redux/actions/adoption/collection";

interface CreateCollectionDialogProps {
  show: boolean;
  list?: string | undefined;
  onClose: () => void;
}

const CreateCollectionDialog: React.FC<CreateCollectionDialogProps> = ({
  show,
  list,
  onClose,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [currentStep, setCurrentStep] = React.useState(0);
  const [name, setName] = React.useState("");
  const [experience, setExperience] = React.useState(DEFAULT_EXPERIENCE);
  const [assetViewer, setAssetViewer] = React.useState(DEFAULT_ASSET_VIEWER);
  const [assetState, setAssetState] = React.useState({ option: null, entities: [] });

  const [error, setError] = React.useState<string | null>(null);

  const defaultExperience = useSelector((state) => (_.get(state, `accounts.item.collectionSettings.viewerType`) || DEFAULT_EXPERIENCE));
  const defaultAssetViewer = useSelector((state) => (_.get(state, `accounts.item.librarySettings.assetViewer`) || DEFAULT_ASSET_VIEWER));

  const creating = useSelector((state) => (_.get(state, `collections.creation.creating`) || false));

  const experiences = useMemo(() => {
    return subscriptionPlanService
      .getCollectionExperiences()
      .map((experienceId) => {
        return EXPERIENCES.find((item) => item.id === experienceId);
      })
      .filter((item) => item);
  }, [subscriptionPlanService.getCollectionExperiences()]);

  const assetViewers = useMemo(() => {
    return subscriptionPlanService
      .getCollectionAssetViewers()
      .map((viewerId) => {
        return ASSET_VIEWERS.find((item) => item.id === viewerId);
      })
      .filter((item) => item);
  }, [subscriptionPlanService.getCollectionAssetViewers()]);

  const nextButtonText = useMemo(() => {
    const finalStep = _.size(assetViewers) == 0 ? 1 : 2;
    if (currentStep === finalStep) {
      if (creating) {
        return "Creating...";
      }

      return "Confirm";
    }

    return "Next";
  }, [creating, currentStep]);

  const createdCollection = useSelector((state) => {
    const finished = _.get(state, `collections.creation.created`) || false;
    if (finished) {
      return _.get(state, `collections.creation.item`) as unknown as Collection;
    }

    return null;
  });

  const {
    trackCreateCollectionClicked,
    trackChangeExperienceClickedEvent,
    trackExperienceTypeSelectedEvent,
    trackViewerTypeSelectedEvent,
    trackCollectionCreatedEvent
  } = useCollectionsAdoption();

  React.useEffect(() => {
    setCurrentStep(0);
    setName("");
    setError(null);
    setExperience(defaultExperience);
    setAssetViewer(defaultAssetViewer);

    if (show) {
      trackCreateCollectionClicked();
    }
  }, [show]);

  React.useEffect(() => {
    setExperience(defaultExperience);
  }, [defaultExperience]);

  React.useEffect(() => {
    setAssetViewer(defaultAssetViewer);
  }, [defaultAssetViewer]);

  React.useEffect(() => {
    if (createdCollection) {
      trackCollectionCreatedEvent(createdCollection);
      navigate(`/collections/${createdCollection.id}`);
      dispatch(invalidateCollectionCreationState());
      onClose();
    }
  }, [createdCollection]);

  const handleClose = () => {
    onClose();
  };

  const handleStepChange = (step: number) => {
    setCurrentStep(step);

    if (step === 1) {
      trackChangeExperienceClickedEvent();
    }
  };

  const handleNameChange = (e) => {
    setName(e.target.value);
    setError(null);
  };

  const handleExperienceChange = (experience: string) => {
    setExperience(experience);

    trackExperienceTypeSelectedEvent({
      experience: experience,
    });
  };

  const handleAssetViewerChange = (viewer: string) => {
    setAssetViewer(viewer);

    trackViewerTypeSelectedEvent({
      viewer: viewer,
    });
  };

  const handleAssetStateChange = (state) => {
    setAssetState(state);
  }

  const handleConfirm = () => {
    const e = validateForm();
    if (e) {
      setError(e);
      return;
    }

    goToNextStep(currentStep);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleConfirm();
  };

  const goToNextStep = (step) => {
    const finalStep = _.size(assetViewers) == 0 ? 1 : 2;
    if (step === finalStep && !creating) {
      handleCreateCollection();
    } else {
      if (step + 1 === 1) {
        trackChangeExperienceClickedEvent();
      }

      setCurrentStep(step + 1);
    }
  };

  const handleCreateCollection = () => {
    const request = {
      name: name,
      list: list,
      viewerType: experience,
      assetViewer: assetViewer,
      assets: [],
    };

    dispatch(createCollection(request));
  };

  const handleCancel = () => {
    onClose();
  };

  const validateForm = () => {
    if (_.isEmpty(name) || _.isEmpty(_.trim(name))) {
      return "Please provide a valid name";
    }

    return null;
  };

  return (
    <Slider.Dialog
      className={styles.createCollectionDialog}
      width={"380px"}
      show={show}
      onClose={handleClose}
    >
      <Slider.Header
        className={styles.header}
        title={
          <>
            creating <br />
            new collection
          </>
        }
      />
      <Slider.Body className={styles.body}>
        <Wizard.Pane
          className={styles.wizard}
          currentStep={currentStep}
          gap={30}
          onStepChange={handleStepChange}
        >
          <Wizard.Step name="Naming" completed={!(_.isEmpty(name) || _.isEmpty(_.trim(name)))}>
            <form
              className={cx(styles.step, styles.nameStep)}
              onSubmit={handleSubmit}
            >
              <Input
                autoFocus
                className={styles.input}
                placeholder="Add a collection name"
                value={name}
                onChange={handleNameChange}
              />
              <Reveal show={!!error}>
                <div className={styles.error}>
                  <i className="fa-solid fa-triangle-exclamation"></i>
                  {error}
                </div>
              </Reveal>

              <p style={{ marginTop: 20, marginRight: 40 }}>
                Start creating your collection here! Select the Collection Experience and Viewer that best suit your audience and content, then personalize it for a truly engaging experience.
              </p>
              <p style={{ marginTop: 20, marginRight: 40 }}>
                For detailed instructions and helpful tips, refer to our support
                articles.
              </p>
            </form>
          </Wizard.Step>
          <Wizard.Step name="Choose experience" completed={true}>
            <div className={cx(styles.step, styles.experienceStep)}>
              <ExperienceSelector
                experiences={experiences}
                selected={experience}
                onSelect={handleExperienceChange}
              />
            </div>
          </Wizard.Step>
          {_.size(assetViewers) > 0 &&
            <Wizard.Step name="Choose Viewer" completed={true}>
              <div className={cx(styles.step, styles.experienceStep)}>
                <AssetViewerSelector
                  viewers={assetViewers}
                  selected={assetViewer}
                  onSelect={handleAssetViewerChange}
                />
              </div>
            </Wizard.Step>
          }
        </Wizard.Pane>
      </Slider.Body>
      <Slider.Actions>
        <Button
          icon="paperflite-tick"
          text={nextButtonText}
          variant="primary"
          disabled={creating}
          onClick={handleConfirm}
        />
        <Button
          text="Cancel"
          variant="secondary"
          disabled={creating}
          onClick={handleCancel}
        />
      </Slider.Actions>
    </Slider.Dialog>
  );
};

interface ExperienceSelectorProps {
  experiences: any[];
  selected: string;
  // eslint-disable-next-line no-unused-vars
  onSelect: (experience: string) => void;
}

const ExperienceSelector: React.FC<ExperienceSelectorProps> = ({
  experiences,
  selected,
  onSelect,
}) => {
  const descriptionTransition = useTransition(selected, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  return (
    <div className={styles.experiences}>
      <p>
        Choose the experience that best showcases your content and resonates with your audience.
      </p>
      <Carousel
        activeIndex={experiences.findIndex((item) => item.id === selected)}
        indicators={false}
        controls={false}
      >
        {experiences.map((experience) => (
          <Carousel.Item key={experience.id}>
            <img src={experience.icon} alt={experience.name} />
          </Carousel.Item>
        ))}
      </Carousel>
      <div className={styles.types}>
        {experiences.map((item) => (
          <button
            key={item.id}
            className={cx(styles.type, {
              [styles.active]: item.id === selected,
            })}
            onClick={() => onSelect(item.id)}
          >
            <div className={styles.name}>{item.name}</div>
          </button>
        ))}
      </div>
      <div className={styles.description}>
        {descriptionTransition((style, item) => {
          const experience = experiences.find((e) => e.id === item);
          return (
            <animated.div style={style} className={styles.inner}>
              {experience.description}
            </animated.div>
          );
        })}
      </div>
    </div>
  );
};

interface AssetViewerSelectorProps {
  viewers: any[];
  selected: string;
  // eslint-disable-next-line no-unused-vars
  onSelect: (viewer: string) => void;
}

const AssetViewerSelector: React.FC<AssetViewerSelectorProps> = ({
  viewers,
  selected,
  onSelect,
}) => {

  const descriptionTransition = useTransition(selected, {
    initial: { opacity: 0 },
    from: { opacity: 0 },
    enter: { opacity: 1 },
    leave: { opacity: 0 },
  });

  return (
    <div className={styles.experiences}>
      <p>
        Select the viewer that best suits your content and audience for this
        collection.
      </p>
      <Carousel
        activeIndex={viewers.findIndex((item) => item.id === selected)}
        indicators={false}
        controls={false}
      >
        {viewers.map((viewer) => (
          <Carousel.Item key={viewer.id}>
            <img src={viewer.icon} alt={viewer.name} />
          </Carousel.Item>
        ))}
      </Carousel>
      <div className={styles.types}>
        {viewers.map((item) => (
          <button
            key={item.id}
            className={cx(styles.type, {
              [styles.active]: item.id === selected,
            })}
            onClick={() => onSelect(item.id)}
          >
            <div className={styles.name}>{item.name}</div>
          </button>
        ))}
      </div>
      <div className={styles.description}>
        {descriptionTransition((style, item) => {
          const viewer = viewers.find((e) => e.id === item);
          if (!viewer) {
            return false;
          }

          return (
            <animated.div style={style} className={styles.inner}>
              {viewer.description}
            </animated.div>
          );
        })}
      </div>
    </div>
  );
};

export { CreateCollectionDialog, AssetViewerSelector, ExperienceSelector };
